import type { RosterChangeCell } from "@/services/roster/changeNotices";
import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import { cn } from "@/utils/cn";
import type { RosterNurse } from "@vTypes/snapshot-type";

/** 실측 앵커: 1칸 67px, 7칸 512px */
const ASSIGNMENT_BAR_MIN_SPAN = 1;
const ASSIGNMENT_BAR_MIN_WIDTH_PX = 67;
const ASSIGNMENT_BAR_MAX_SPAN = 7;
const ASSIGNMENT_BAR_MAX_WIDTH_PX = 512;

function assignmentBarWidthPx(spanDays: number): number {
  if (spanDays <= ASSIGNMENT_BAR_MIN_SPAN) return ASSIGNMENT_BAR_MIN_WIDTH_PX;
  if (spanDays >= ASSIGNMENT_BAR_MAX_SPAN) return ASSIGNMENT_BAR_MAX_WIDTH_PX;

  // 1칸~7칸 구간을 선형 보간해서 마진/경계 누적 오차를 완화.
  const perGap =
    (ASSIGNMENT_BAR_MAX_WIDTH_PX - ASSIGNMENT_BAR_MIN_WIDTH_PX) /
    (ASSIGNMENT_BAR_MAX_SPAN - ASSIGNMENT_BAR_MIN_SPAN);
  const raw =
    ASSIGNMENT_BAR_MIN_WIDTH_PX + (spanDays - ASSIGNMENT_BAR_MIN_SPAN) * perGap;
  return Math.round(raw);
}

function ViewRosterDay({
  day,
  holiday,
  myShift,
  /** 카탈로그 기준 표시용(예: `shift_id`). 없으면 `myShift` 원문 사용 */
  displayShiftId,
  color,
  coworkerList,
  serverCoworkers,
  muted,
  assignmentBar,
  statusLabel,
  compact = false,
  shiftName,
  selected = false,
  changeCell,
  onSelect,
}: {
  serverCoworkers?: readonly {
    readonly nurse_id: string;
    readonly name: string;
  }[];
  compact?: boolean;
  selected?: boolean;
  changeCell?: RosterChangeCell;
  onSelect?: () => void;
  shiftName?: string;
  statusLabel?: string;
  day: number;
  holiday: { weekend: number; isHol: boolean };
  myShift: string | null;
  displayShiftId?: string | null;
  color: string;
  coworkerList: RosterNurse[] | undefined;
  muted?: boolean;
  /** 파견 등 — `w_selectCalendar_date`(시프트 영역) 기준 `absolute` 하단 바 */
  assignmentBar?: {
    label: string;
    color: string;
    spanDays: number;
    muted?: boolean;
  } | null;
}) {
  const normalizedShift = (myShift ?? "").trim();
  const hasShift = normalizedShift !== "" && normalizedShift !== "-";
  const hasColor = !!color && color !== "transparent";
  const label =
    (displayShiftId ?? "").trim() || (hasShift ? normalizedShift : "");
  const coworkerSummary = serverCoworkers
    ? serverCoworkers.length === 0
      ? "함께 근무 0명"
      : `${serverCoworkers[0].name || serverCoworkers[0].nurse_id}${serverCoworkers.length > 1 ? ` 외 ${serverCoworkers.length - 1}명` : ""}`
    : null;
  const contentMutedClass = muted ? "opacity-10 saturate-0" : "";

  return (
    <div
      className={`
                ${holiday.weekend === 6 ? "!text-text-sat" : holiday.weekend === 0 ? "!text-text-sun" : ""}
                ${holiday.isHol && "text-text-holiday"}
               roster-view-calendar__day roster-view-calendar__coworker-wrap !text-text-sub
         ${!compact && hasShift && hasColor && "!text-white"}
            w-full min-w-0
            `}
    >
      {onSelect && (
        <button
          type="button"
          aria-label={`${day}일 근무 상세 보기${changeCell ? ` · 변경 ${changeCell.before?.shift_id || "빈 칸"} → ${changeCell.after?.shift_id || "빈 칸"}` : ""}`}
          aria-pressed={selected}
          onClick={onSelect}
          className={cn(
            "absolute inset-0 z-[1] rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sig",
            selected && "bg-sig/5 ring-2 ring-inset ring-sig"
          )}
        />
      )}
      <div
        className={cn(
          "roster-view-calendar__date",
          onSelect && "z-[2]",
          compact ? "!p-2" : "!p-1",
          contentMutedClass
        )}
        style={
          compact
            ? {
                color: holiday.isHol
                  ? "#ed1c24"
                  : holiday.weekend === 6
                    ? "#72bfff"
                    : holiday.weekend === 0
                      ? "#ff88cc"
                      : "#545454",
              }
            : undefined
        }
      >
        {day}
      </div>
      <div
        className={cn(
          "roster-view-calendar__shift",
          changeCell && "roster-reissue-code",
          onSelect && "pointer-events-none z-[2]",
          !compact && "text-3xl!",
          !hasShift && "bg-muted! text-accent-foreground/10!",
          contentMutedClass
        )}
        data-reissue-cell={changeCell ? day : undefined}
        title={
          changeCell
            ? `변경 ${changeCell.before?.shift_id || "빈 칸"} → ${changeCell.after?.shift_id || "빈 칸"}`
            : undefined
        }
        style={{
          backgroundColor: compact
            ? `color-mix(in srgb, ${color || "#d6d6de"} 15%, white)`
            : color,
          color: getReadableShiftCodeTextColor(color),
        }}
      >
        {compact && hasShift ? (
          <>
            <span
              className="flex size-5 shrink-0 items-center justify-center rounded-sm text-[11px] font-bold"
              style={{
                backgroundColor: color,
                color: getReadableShiftCodeTextColor(color),
              }}
            >
              {label}
            </span>
            <span
              className="min-w-0 flex-1 truncate text-[12px] font-semibold text-text-main2"
              title={shiftName}
            >
              {shiftName || label}
            </span>
          </>
        ) : hasShift ? (
          label
        ) : (
          "-"
        )}
        {statusLabel && (
          <span
            className="absolute right-1 top-1 rounded bg-background px-1 text-[10px] text-foreground"
            data-testid="roster-cell-status"
          >
            {statusLabel}
          </span>
        )}
        {coworkerList && coworkerList.length > 0 && (
          <div className="roster-view-calendar__coworker-list">
            <ul>
              {coworkerList.map((n, index) => {
                return <li key={index}>{n.name}</li>;
              })}
            </ul>
          </div>
        )}
      </div>
      {compact && coworkerSummary !== null && (
        <div
          className="pointer-events-none absolute left-[7px] right-[7px] top-[64px] z-[2] truncate text-left text-[11px] leading-4 text-muted-foreground"
          title={
            serverCoworkers
              ?.map((nurse) => nurse.name || nurse.nurse_id)
              .join(", ") || coworkerSummary
          }
          aria-label={`함께 근무하는 사람: ${coworkerSummary}`}
        >
          {coworkerSummary}
        </div>
      )}
      {assignmentBar != null &&
        assignmentBar.label !== "" &&
        assignmentBar.spanDays >= 1 && (
          <div
            className={cn(
              "pointer-events-none absolute bottom-0 left-0 z-[5] flex h-4 min-w-0 max-w-none items-center justify-center text-center text-[10px] font-medium transition-opacity",
              assignmentBar.muted && "opacity-20"
            )}
            role="img"
            aria-label={`${assignmentBar.label} · ${assignmentBar.spanDays}일${statusLabel ? ` · ${statusLabel}` : ""}`}
            title={assignmentBar.label}
            style={{
              width: compact
                ? `calc(${assignmentBar.spanDays * 100}% + ${(assignmentBar.spanDays - 1) * 7}px - 2px)`
                : `calc(${assignmentBarWidthPx(assignmentBar.spanDays)}px - 8px)`,
              color: assignmentBar.color,
            }}
          >
            <span
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0 border-t-2 border-current"
              data-ward-band-line
            />
            <span className="pointer-events-auto absolute inset-x-0 bottom-[4px] block truncate px-1 text-center leading-3">
              {assignmentBar.label}
            </span>
          </div>
        )}
    </div>
  );
}
export default ViewRosterDay;
